import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { Plus, Eye, Search, RefreshCw, AlertCircle } from "lucide-react";
import {
  StatusPill, SlideOver, Modal, StatsRow,
} from "@/components/phase4/Phase4Common";

const STATUS_OPTIONS = [
  "ALL", "OPEN", "ACTION_REQUIRED", "RE_ATTEMPT_SCHEDULED",
  "RTO_INITIATED", "RESOLVED", "CLOSED",
];
const REASONS = [
  "ADDRESS_NOT_FOUND", "CUSTOMER_NOT_AVAILABLE", "REFUSED_DELIVERY",
  "PAYMENT_NOT_READY", "INCORRECT_ADDRESS", "ACCESS_RESTRICTED",
  "NATURAL_CALAMITY", "FUTURE_DELIVERY_REQUESTED", "OTHER",
];
const CUSTOMER_INSTRUCTIONS = [
  "RE_ATTEMPT", "UPDATE_ADDRESS", "AUTHORIZE_NEIGHBOUR",
  "RTO_REQUESTED", "FUTURE_DATE",
];

export default function NdrConsole() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selected, setSelected] = useState(null);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [stats, setStats] = useState({ open: 0, action: 0, scheduled: 0, rto: 0, resolved: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 100 };
      if (filterStatus !== "ALL") params.status = filterStatus;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const r = await api.get("/v2/ndr", { params });
      let list = r.data?.items || [];
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        list = list.filter((n) =>
          (n.awb || "").toLowerCase().includes(q) ||
          (n.lr_number || "").toLowerCase().includes(q) ||
          (n.ndr_id || "").toLowerCase().includes(q)
        );
      }
      setItems(list);
      // Compute stats from a separate unfiltered call
      const all = await api.get("/v2/ndr", { params: { limit: 1000 } });
      const allItems = all.data?.items || [];
      const today = new Date(); today.setHours(0, 0, 0, 0);
      setStats({
        open: allItems.filter((n) => n.status === "OPEN").length,
        action: allItems.filter((n) => n.status === "ACTION_REQUIRED").length,
        scheduled: allItems.filter((n) => n.status === "RE_ATTEMPT_SCHEDULED").length,
        rto: allItems.filter((n) => n.status === "RTO_INITIATED").length,
        resolved: allItems.filter((n) => {
          if (n.status !== "RESOLVED") return false;
          const ra = n.resolved_at ? new Date(n.resolved_at) : null;
          return ra && ra >= today;
        }).length,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load NDR cases");
    }
    setLoading(false);
  }, [filterStatus, search, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="ndr-console">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">NDR Console</h1>
            <p className="text-slate-500 text-sm mt-0.5">Non-Delivery Report Management</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded inline-flex items-center gap-1">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={() => setRaiseOpen(true)}
              data-testid="ndr-raise-btn"
              className="px-3 py-1.5 bg-brand-500 hover:bg-brand-700 text-white text-xs font-semibold rounded inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Raise NDR
            </button>
          </div>
        </div>

        <StatsRow items={[
          { key: "open", label: "Open", value: stats.open, color: "text-red-600" },
          { key: "action", label: "Action Required", value: stats.action, color: "text-amber-600" },
          { key: "scheduled", label: "Re-attempt", value: stats.scheduled, color: "text-blue-600" },
          { key: "rto", label: "RTO Initiated", value: stats.rto, color: "text-orange-600" },
          { key: "resolved", label: "Resolved Today", value: stats.resolved, color: "text-emerald-600" },
        ]} />

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2 items-end">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="ndr-filter-status"
              className="text-xs border border-slate-200 rounded px-2 py-1.5">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="text-xs border border-slate-200 rounded px-2 py-1.5" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="text-xs border border-slate-200 rounded px-2 py-1.5" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Search AWB / LR</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                data-testid="ndr-search"
                placeholder="AWB or LR…"
                className="w-full text-xs border border-slate-200 rounded pl-7 pr-2 py-1.5" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="ndr-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["NDR ID", "AWB", "LR", "Customer", "Reason", "Attempt #", "Status", "Created", "Actions"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-bold text-slate-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-slate-400">No NDR cases found.</td></tr>
              ) : items.map((n) => (
                <tr key={n.ndr_id} className="hover:bg-slate-50" data-testid={`ndr-row-${n.ndr_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{n.ndr_id}</td>
                  <td className="px-3 py-2 font-mono text-xs">{n.awb || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{n.lr_number || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{n.customer_id?.slice(-6) || "—"}</td>
                  <td className="px-3 py-2 text-xs">{(n.reason || "").replace(/_/g, " ")}</td>
                  <td className="px-3 py-2 text-xs text-center">{n.attempt_number}</td>
                  <td className="px-3 py-2"><StatusPill status={n.status} /></td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">
                    {n.created_at ? new Date(n.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => setSelected(n)}
                      data-testid={`ndr-view-${n.ndr_id}`}
                      className="text-brand-500 hover:text-brand-700"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <NdrDetailPanel
          ndr={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { load(); setSelected(null); }}
        />
      )}
      {raiseOpen && (
        <RaiseNdrModal
          onClose={() => setRaiseOpen(false)}
          onCreated={() => { load(); setRaiseOpen(false); }}
        />
      )}
    </DashboardLayout>
  );
}

function NdrDetailPanel({ ndr, onClose, onChanged }) {
  const [resolveOpen, setResolveOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [rtoOpen, setRtoOpen] = useState(false);
  const [resolution, setResolution] = useState("");
  const [escalateReason, setEscalateReason] = useState("");
  const [busy, setBusy] = useState(false);

  // Customer action form
  const [instr, setInstr] = useState("");
  const [reAttemptDate, setReAttemptDate] = useState("");
  const [updatedAddress, setUpdatedAddress] = useState("");

  const ops = async (action, body = {}) => {
    setBusy(true);
    try {
      await api.put(`/v2/ndr/${ndr.ndr_id}/ops-action`, { action, ...body });
      toast.success(`Action ${action} done`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const customerSubmit = async () => {
    if (!instr) { toast.error("Select an instruction"); return; }
    setBusy(true);
    try {
      const body = { customer_instruction: instr };
      if (instr === "RE_ATTEMPT" && reAttemptDate) body.re_attempt_date = new Date(reAttemptDate).toISOString();
      if (instr === "UPDATE_ADDRESS" && updatedAddress) body.updated_address = updatedAddress;
      await api.put(`/v2/ndr/${ndr.ndr_id}/customer-action`, body);
      toast.success("Customer instruction saved");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  return (
    <SlideOver open onClose={onClose} title={`NDR ${ndr.ndr_id}`} testId="ndr-detail-panel">
      <div className="space-y-4 text-sm">
        <div className="flex items-center gap-2">
          <StatusPill status={ndr.status} />
          {ndr.escalated && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-rose-100 text-rose-700">ESCALATED</span>}
        </div>

        {/* Section 1 — Shipment Info */}
        <Section title="Shipment Info">
          <Row k="AWB" v={<span className="font-mono">{ndr.awb || "—"}</span>} />
          <Row k="LR Number" v={<span className="font-mono">{ndr.lr_number || "—"}</span>} />
          <Row k="Customer" v={ndr.customer_id || "—"} />
          <Row k="Attempt #" v={ndr.attempt_number} />
          <Row k="Attempted at" v={ndr.attempted_at ? new Date(ndr.attempted_at).toLocaleString() : "—"} />
        </Section>

        {/* Section 2 — Reason */}
        <Section title="NDR Reason">
          <div className="inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full border bg-slate-100 text-slate-700 mb-2">
            {(ndr.reason || "").replace(/_/g, " ")}
          </div>
          <p className="text-slate-700 text-xs whitespace-pre-wrap">{ndr.reason_detail || "—"}</p>
        </Section>

        {/* Section 3 — Customer Instruction */}
        <Section title="Customer Response">
          {!ndr.customer_instruction ? (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
              Awaiting customer response.
            </div>
          ) : (
            <div className="space-y-1">
              <span className={`inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full border ${ndr.customer_instruction === "RTO_REQUESTED" ? "bg-red-100 text-red-700 border-red-200" : "bg-blue-100 text-blue-700 border-blue-200"}`}>
                {ndr.customer_instruction.replace(/_/g, " ")}
              </span>
              {ndr.re_attempt_date && <Row k="Re-attempt date" v={new Date(ndr.re_attempt_date).toLocaleString()} />}
              {ndr.updated_address && <Row k="Updated address" v={ndr.updated_address} />}
            </div>
          )}
        </Section>

        {/* Section 4 — Ops Actions */}
        <Section title="Ops Actions">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setResolveOpen(true)} disabled={busy}
              data-testid="ndr-action-resolve"
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded">Resolve</button>
            <button onClick={() => setEscalateOpen(true)} disabled={busy}
              data-testid="ndr-action-escalate"
              className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded">Escalate</button>
            <button onClick={() => setRtoOpen(true)} disabled={busy}
              data-testid="ndr-action-rto"
              className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded">Initiate RTO</button>
          </div>
          {ndr.resolution && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">
              <span className="font-semibold text-emerald-700">Resolved:</span> {ndr.resolution}
            </div>
          )}
        </Section>

        {/* Section 5 — Customer Action Form */}
        <Section title="Record Customer Action">
          <div className="space-y-2">
            <select value={instr} onChange={(e) => setInstr(e.target.value)}
              data-testid="ndr-customer-instr"
              className="w-full text-xs border border-slate-200 rounded px-2 py-1.5">
              <option value="">— Select instruction —</option>
              {CUSTOMER_INSTRUCTIONS.map((i) => <option key={i} value={i}>{i.replace(/_/g, " ")}</option>)}
            </select>
            {instr === "RE_ATTEMPT" && (
              <input type="datetime-local" value={reAttemptDate} onChange={(e) => setReAttemptDate(e.target.value)}
                data-testid="ndr-reattempt-date"
                className="w-full text-xs border border-slate-200 rounded px-2 py-1.5" />
            )}
            {instr === "UPDATE_ADDRESS" && (
              <textarea value={updatedAddress} onChange={(e) => setUpdatedAddress(e.target.value)} rows={2}
                data-testid="ndr-updated-address"
                placeholder="New address…"
                className="w-full text-xs border border-slate-200 rounded px-2 py-1.5" />
            )}
            <button onClick={customerSubmit} disabled={busy || !instr}
              data-testid="ndr-customer-submit"
              className="px-3 py-1.5 bg-brand-500 hover:bg-brand-700 text-white text-xs font-semibold rounded disabled:opacity-50">
              Submit Customer Action
            </button>
          </div>
        </Section>
      </div>

      <Modal open={resolveOpen} onClose={() => setResolveOpen(false)} title="Resolve NDR" testId="ndr-resolve-modal">
        <textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={4}
          placeholder="Resolution notes…"
          className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={() => setResolveOpen(false)} className="px-3 py-1.5 border border-slate-200 text-xs rounded">Cancel</button>
          <button onClick={async () => { await ops("RESOLVE", { resolution }); setResolveOpen(false); }}
            className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded">Resolve</button>
        </div>
      </Modal>

      <Modal open={escalateOpen} onClose={() => setEscalateOpen(false)} title="Escalate NDR" testId="ndr-escalate-modal">
        <textarea value={escalateReason} onChange={(e) => setEscalateReason(e.target.value)} rows={3}
          placeholder="Escalation reason…"
          className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={() => setEscalateOpen(false)} className="px-3 py-1.5 border border-slate-200 text-xs rounded">Cancel</button>
          <button onClick={async () => { await ops("ESCALATE", { escalation_reason: escalateReason }); setEscalateOpen(false); }}
            className="px-3 py-1.5 bg-amber-500 text-white text-xs font-semibold rounded">Escalate</button>
        </div>
      </Modal>

      <Modal open={rtoOpen} onClose={() => setRtoOpen(false)} title="Confirm RTO" testId="ndr-rto-modal">
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs flex gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            This will trigger RTO for AWB <span className="font-mono">{ndr.awb}</span> and auto-calculate demurrage. Confirm?
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={() => setRtoOpen(false)} className="px-3 py-1.5 border border-slate-200 text-xs rounded">Cancel</button>
          <button onClick={async () => { await ops("INITIATE_RTO", { resolution: "Manual RTO from console" }); setRtoOpen(false); }}
            className="px-3 py-1.5 bg-orange-600 text-white text-xs font-semibold rounded">Confirm RTO</button>
        </div>
      </Modal>
    </SlideOver>
  );
}

function RaiseNdrModal({ onClose, onCreated }) {
  const [shipQuery, setShipQuery] = useState("");
  const [shipment, setShipment] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonDetail, setReasonDetail] = useState("");
  const [attempt, setAttempt] = useState(1);
  const [attemptedAt, setAttemptedAt] = useState("");
  const [busy, setBusy] = useState(false);

  const findShipment = async () => {
    if (!shipQuery.trim()) return;
    try {
      const r = await api.get(`/v2/tracking/${encodeURIComponent(shipQuery.trim())}`);
      if (r.data?.shipment_id) {
        setShipment({
          id: r.data.shipment_id,
          awb: r.data.awb || shipQuery.trim(),
          lr: r.data.lr_number,
        });
        toast.success("Shipment found");
      }
    } catch {
      toast.error("Shipment not found");
    }
  };

  const submit = async () => {
    if (!shipment || !reason) { toast.error("Fill all required fields"); return; }
    setBusy(true);
    try {
      await api.post("/v2/ndr", {
        shipment_id: shipment.id,
        reason,
        reason_detail: reasonDetail,
        attempt_number: parseInt(attempt, 10),
        attempted_at: attemptedAt ? new Date(attemptedAt).toISOString() : undefined,
      });
      toast.success("NDR raised");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  return (
    <Modal open onClose={onClose} title="Raise NDR" testId="ndr-raise-modal">
      <div className="space-y-3 text-sm">
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Shipment AWB / LR *</label>
          <div className="flex gap-2">
            <input value={shipQuery} onChange={(e) => setShipQuery(e.target.value)}
              data-testid="ndr-raise-ship-search"
              placeholder="AWB or LR…"
              className="flex-1 text-sm border border-slate-200 rounded px-2 py-1.5" />
            <button onClick={findShipment} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-xs font-semibold rounded">Find</button>
          </div>
          {shipment && (
            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded p-2 text-xs" data-testid="ndr-raise-ship-found">
              ✓ {shipment.awb} {shipment.lr ? `· ${shipment.lr}` : ""}
            </div>
          )}
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Reason *</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="ndr-raise-reason"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5">
            <option value="">— Select —</option>
            {REASONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Reason Detail</label>
          <textarea value={reasonDetail} onChange={(e) => setReasonDetail(e.target.value)} rows={3}
            data-testid="ndr-raise-detail"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Attempt # *</label>
            <select value={attempt} onChange={(e) => setAttempt(e.target.value)}
              data-testid="ndr-raise-attempt"
              className="w-full text-sm border border-slate-200 rounded px-2 py-1.5">
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Attempted At</label>
            <input type="datetime-local" value={attemptedAt} onChange={(e) => setAttemptedAt(e.target.value)}
              data-testid="ndr-raise-at"
              className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-1.5 border border-slate-200 text-xs rounded">Cancel</button>
        <button onClick={submit} disabled={busy || !shipment || !reason}
          data-testid="ndr-raise-submit"
          className="px-4 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded disabled:opacity-50">
          Raise NDR
        </button>
      </div>
    </Modal>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <h4 className="text-[10px] font-bold uppercase text-slate-500 mb-2">{title}</h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-800 font-medium">{v}</span>
    </div>
  );
}
